import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Eye, PlusCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { apiFetch } from '../../utils/api';

export default function ArticlesList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchArticles = () => {
    apiFetch('/articles/all')
      .then(r => r.json())
      .then(data => {
        setArticles(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      const res = await apiFetch(`/articles/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Article deleted');
        fetchArticles();
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="articles-list-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline text-[#6B7280] mb-2">Manage</p>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0A1128] tracking-tight">Articles</h1>
        </div>
        <Link to="/admin/articles/new">
          <Button className="rounded-none bg-[#0A1128] text-white uppercase tracking-widest text-xs hover:bg-[#0A1128]/90" data-testid="new-article-btn">
            <PlusCircle className="w-4 h-4 mr-2" /> New Article
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-24">
          <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-24 border border-[#E5E7EB]">
          <p className="text-[#6B7280] text-sm mb-4">No articles yet</p>
          <Link to="/admin/articles/new">
            <Button className="rounded-none bg-[#0A1128] text-white uppercase tracking-widest text-xs" data-testid="first-article-btn">
              Create Your First Article
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map(article => (
            <div key={article.id} className="border border-[#E5E7EB] p-4 flex items-center gap-4" data-testid={`admin-article-${article.id}`}>
              {article.cover_image && (
                <img src={article.cover_image} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-[#0A1128] truncate">{article.title}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs uppercase tracking-widest text-[#6B7280]">{article.category?.replace('-', ' ')}</span>
                  <span className={`text-xs px-2 py-0.5 uppercase tracking-widest ${article.status === 'published' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                    {article.status}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {article.status === 'published' && (
                  <Button variant="ghost" size="sm" onClick={() => window.open(`/article/${article.slug}`, '_blank')} data-testid={`view-article-${article.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/articles/${article.id}`)} data-testid={`edit-article-${article.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(article.id)} className="text-red-500" data-testid={`delete-article-${article.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
